import {
  useEffect, useState, useRef, useCallback, useContext
} from "react";
import type { Business } from "lens-platform-sdk";
import { LensSDKException } from "lens-platform-sdk";
import { TokenContext } from "src/providers/token-provider";
import { useLensPlatformClient } from "./useLensPlatformClient";

type FilterBy = (business: Business) => boolean;

/**
 * Get user's business entities (Lens Business IDs)
 */
export const useGetBusinesses = (filterBy?: FilterBy) => {
  const lensPlatformClient = useLensPlatformClient();

  const [businesses, setBusinesses] = useState<Business[]>([]);

  const [error, setError] = useState<LensSDKException | null>(null);
  const [loading, setLoading] = useState(false);
  const { token } = useContext(TokenContext);

  // Keep up with mounted/unmounted state
  const mounted = useRef(false);

  useEffect(() => {
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  const getBusiness = useCallback(async () => {
    try {
      if (mounted.current) {
        setLoading(true);
      }

      if (!token) {
        return;
      }

      const response = await lensPlatformClient.business.getMany();

      if (mounted.current) {
        setBusinesses(filterBy ? response.filter(filterBy) : response);
        setError(null);
      }
    } catch (exception) {
      if (mounted.current && exception instanceof LensSDKException) {
        setError(exception);
      }
    } finally {
      if (mounted.current) {
        setLoading(false);
      }
    }
  }, [lensPlatformClient.business, filterBy, token]);

  useEffect(() => {
    // Load businesses on mount
    getBusiness();
  }, [getBusiness]);

  return {
    loading,
    error,
    businesses,
    reload: getBusiness
  };
};
